// Számlázási-profil űrlap tiszta segédei (nem-komponens): a form-értékek típusa, a betöltött sor → form-értékek
// leképezés, a form → wire-body építő, a resolver-séma (kliens-oldali elő-ellenőrzés) és a lokalizált zod-
// hibatérkép. Külön fájl a komponenstől, hogy a react-refresh (Fast Refresh) tiszta maradjon. A minta a
// features/invoices/form.ts + features/assets/form.ts.
//
// Az e-mailek a UI-ban vesszős STRING-ként élnek (több cím) — a wire `string[]`-re parse-olja (a contract
// billingEmails email-tömbje). A fizetési határidő üres → null (a contract preprocess-e is ezt teszi).
import {
  type BillingProfileCreateBodySchema,
  type BillingProfileRowSchema,
  type BillingProfileWriteBodySchema,
} from '@atlas/contracts';
import { type TFunction } from 'i18next';

import { type DirectorEntry } from './directors';
import { type z } from 'zod';
import { z as zod } from 'zod';

type BillingProfileRow = z.infer<typeof BillingProfileRowSchema>;
type WriteBody = z.input<typeof BillingProfileWriteBodySchema>;
type CreateBody = z.input<typeof BillingProfileCreateBodySchema>;

/** Egy ügyvezető-sor az űrlapon — csupa string (üres = nincs megadva), hogy a mezők kontrolláltak legyenek. */
export interface DirectorFormValue {
  name: string;
  startsOn: string;
  endsOn: string;
}

/** Üres ügyvezető-sor (a „hozzáadás" gomb ezt fűzi a végére). */
export const EMPTY_DIRECTOR: DirectorFormValue = { name: '', startsOn: '', endsOn: '' };

/** A számlázási-profil form (mind string/bool-alapú; a `billingEmails` vesszős STRING). */
export interface BillingProfileFormValues {
  /** A cél-ügyfél (create-kor választva; edit-kor zárolt — a sorból). */
  clientId: string;
  billingName: string;
  taxNumber: string;
  euTaxNumber: string;
  countryCode: string;
  zipCode: string;
  city: string;
  address: string;
  /** Ügyvezető-történet. Az űrlapon a dátumok ÜRES STRINGEK (a `DateField` ezt adja), a wire-en `null`. */
  managingDirectors: DirectorFormValue[];
  /** Vesszővel/pontosvesszővel/sortöréssel tagolt e-mail lista (a wire `string[]`-re parse-olja). */
  billingEmails: string;
  /** Fizetési határidő napokban (üres → null); a resolver 1–31 közé szorítja, ha van érték. */
  defaultInvoicePaymentDeadlineDays: string;
  /** Alapértelmezett számlakép-megjegyzések — a legacy KÉT külön mezője; a kettő a nyomtatott számlán MÁS
   *  HELYEN jelenik meg (a számla-sor is két oszlopban tartja). */
  defaultInvoiceNote: string;
  isPrimary: boolean;
}

/** Üres form (create-mód default): HU országkód, üres mezők, nem-elsődleges (az első profil auto-primary a BFF-ben). */
export const EMPTY_BILLING_PROFILE_FORM: BillingProfileFormValues = {
  clientId: '',
  billingName: '',
  taxNumber: '',
  euTaxNumber: '',
  countryCode: 'HU',
  zipCode: '',
  city: '',
  address: '',
  managingDirectors: [],
  billingEmails: '',
  defaultInvoicePaymentDeadlineDays: '',
  defaultInvoiceNote: '',
  isPrimary: false,
};

/** Vesszős/pontosvesszős/soremeléses input → trimmelt, üres-mentes e-mail tömb. */
export function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/** A betöltött profil-sor → form-értékek (edit-mód). */
export function billingProfileToFormValues(row: BillingProfileRow): BillingProfileFormValues {
  return {
    clientId: row.clientId,
    billingName: row.billingName ?? '',
    taxNumber: row.taxNumber ?? '',
    euTaxNumber: row.euTaxNumber ?? '',
    countryCode: row.countryCode ?? 'HU',
    zipCode: row.zipCode ?? '',
    city: row.city ?? '',
    address: row.address ?? '',
    managingDirectors: row.managingDirectors.map((d) => ({
      name: d.name,
      startsOn: d.startsOn ?? '',
      endsOn: d.endsOn ?? '',
    })),
    billingEmails: row.billingEmails.join(', '),
    defaultInvoicePaymentDeadlineDays:
      row.defaultInvoicePaymentDeadlineDays === null
        ? ''
        : String(row.defaultInvoicePaymentDeadlineDays),
    defaultInvoiceNote: row.defaultInvoiceNote ?? '',
    isPrimary: row.isPrimary,
  };
}

const nullable = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/**
 * Űrlap-sorok → wire-bejegyzések. A NÉV NÉLKÜLI sorok KIMARADNAK: a „hozzáadás" gomb üres sort fűz a
 * listához, és ha a felhasználó meggondolja magát, egy üres sor miatt nem bukhat el a mentés (a szerződés
 * a nevet kötelezőnek írja). Ez tehát nem néma adat-eldobás, hanem a „még nem töltöttem ki" kezelése.
 */
function toDirectorEntries(rows: DirectorFormValue[]): DirectorEntry[] {
  return rows
    .filter((r) => r.name.trim() !== '')
    .map((r) => ({
      name: r.name.trim(),
      startsOn: nullable(r.startsOn),
      endsOn: nullable(r.endsOn),
    }));
}

/** A közös write-body (create törzse + update). A kliens a snapshot-mezőket küldi; a `clientId`-t a busz injektálja. */
export function billingProfileFormToWriteBody(values: BillingProfileFormValues): WriteBody {
  const deadlineRaw = values.defaultInvoicePaymentDeadlineDays.trim();
  return {
    billingName: values.billingName.trim(),
    taxNumber: nullable(values.taxNumber),
    euTaxNumber: nullable(values.euTaxNumber),
    countryCode: values.countryCode.trim().toUpperCase(),
    zipCode: values.zipCode.trim(),
    city: values.city.trim(),
    address: values.address.trim(),
    managingDirectors: toDirectorEntries(values.managingDirectors),
    billingEmails: parseEmails(values.billingEmails),
    defaultInvoicePaymentDeadlineDays: deadlineRaw === '' ? null : Number(deadlineRaw),
    defaultInvoiceNote: nullable(values.defaultInvoiceNote),
    isPrimary: values.isPrimary,
  };
}

/** Create-body: a write-törzs + a cél-ügyfél (CSAK create-nél). */
export function billingProfileFormToCreateBody(values: BillingProfileFormValues): CreateBody {
  return { ...billingProfileFormToWriteBody(values), clientId: values.clientId };
}

// ── Resolver-séma (kliens-oldali elő-ellenőrzés; a szerver a végső fal) ──

/**
 * A form-input validálása. A hiba-KÓDOK (message string) a `buildBillingProfileErrorMap`-ben fordulnak a
 * `validation:billingProfiles.*` rétegre. A `clientId` csak create-kor kötelező — ezt a hívó `superRefine`-nel
 * NEM tudja tudni (nincs mód-flag a form-ban), ezért a clientId-t a komponens create-módban külön ellenőrzi
 * (setError), a séma itt csak a mindig-kötelező mezőket fogja.
 */
// FONTOS: a séma NEM ad át üzenet-stringet a check-eknek (`.min(1)`, NEM `.min(1, 'kulcs')`), a custom
// issue is üzenet NÉLKÜL adódik hozzá — különben a Zod a séma explicit üzenetét használná, és a lenti
// (lokalizáló) hibatérkép SOHA nem futna le (a nyers „deadline"/„billingName" kulcs látszana). A fordítást
// a `buildBillingProfileErrorMap` végzi a `path`+`code` alapján (az org-structure mintája).
export const billingProfileFormSchema = zod.object({
  clientId: zod.string(),
  billingName: zod.string().trim().min(1).max(255),
  taxNumber: zod.string().max(50),
  euTaxNumber: zod.string().max(50),
  countryCode: zod.string().trim().length(2),
  zipCode: zod.string().trim().min(1).max(20),
  city: zod.string().trim().min(1).max(255),
  address: zod.string().trim().min(1).max(500),
  // A név nélküli sorok a wire-alakból kiesnek (l. `toDirectorEntries`), ezért a resolver is ÁTENGEDI
  // őket — különben egy véletlenül hozzáadott üres sor blokkolná a mentést. A kitöltött sorokra viszont
  // ugyanaz a két szabály fut, mint a szerveren: hossz-korlát és nem-fordított időszak.
  managingDirectors: zod.array(
    zod
      .object({
        name: zod.string().max(255),
        startsOn: zod.string(),
        endsOn: zod.string(),
      })
      .superRefine((row, ctx) => {
        if (row.name.trim() === '') return;
        if (row.startsOn !== '' && row.endsOn !== '' && row.endsOn < row.startsOn) {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['endsOn'] });
        }
      }),
  ),
  billingEmails: zod.string().superRefine((raw, ctx) => {
    for (const email of parseEmails(raw)) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        ctx.addIssue({ code: zod.ZodIssueCode.custom });
        break;
      }
    }
  }),
  defaultInvoicePaymentDeadlineDays: zod.string().superRefine((raw, ctx) => {
    if (raw.trim() === '') return;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > 31) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom });
    }
  }),
  defaultInvoiceNote: zod.string().max(5000),
  isPrimary: zod.boolean(),
});

/**
 * Lokalizált zod-hibatérkép: a MEZŐ (`issue.path[0]`) + a hiba-KÓD alapján a `validation:billingProfiles.*`
 * rétegre fordít (az org-structure mintája). A séma NEM ad üzenetet, így ez a térkép fut le minden issue-ra.
 * A `too_big` (max túllépés) minden hossz-korlátos mezőn a generikus „túl hosszú".
 */
export function buildBillingProfileErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = typeof issue.path[0] === 'string' ? issue.path[0] : '';
    const msg = (k: string): { message: string } => ({
      message: t(`validation:billingProfiles.${k}`),
    });
    const tooLong = issue.code === zod.ZodIssueCode.too_big;
    switch (field) {
      case 'billingName':
        return tooLong ? msg('tooLong') : msg('billingName');
      case 'countryCode':
        return msg('country'); // length(2): a túl rövid ÉS túl hosszú is „országkód" hiba
      case 'zipCode':
        return tooLong ? msg('tooLong') : msg('zip');
      case 'city':
        return tooLong ? msg('tooLong') : msg('city');
      case 'address':
        return tooLong ? msg('tooLong') : msg('address');
      case 'billingEmails':
        return msg('email');
      case 'defaultInvoicePaymentDeadlineDays':
        return msg('deadline');
      case 'managingDirectors':
        // `path`: ['managingDirectors', <index>, <mező>] — a túl hosszú név és a fordított időszak.
        return tooLong ? msg('tooLong') : msg('directorPeriod');
      case 'taxNumber':
      case 'euTaxNumber':
      case 'defaultInvoiceNote':
        return msg('tooLong');
    }
    return { message: ctx.defaultError };
  };
}
